import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, tap } from 'rxjs';
import { S3Image } from '../../models/s3';

export interface GungGroupBranding {
  id: string;
  colors?: { [s: string]: string } | null;
  logo?: S3Image;
  extra?: { [s: string]: any };
}

@Injectable({ providedIn: 'root' })
export class GungGroupBrandingService {
  protected serviceUrl = 'json/group-branding';
  protected brandingSubject: BehaviorSubject<GungGroupBranding[]>;
  private publicCached: GungGroupBranding[];

  constructor(protected http: HttpClient) {}

  /**
   * All group brandings, served from an in-memory BehaviorSubject that lives on the root singleton
   * and therefore survives components being destroyed/recreated on settings-tab switches. It only
   * hits the backend on first access and after a save (see save()), so switching tabs is free, and
   * subscribers refresh reactively when a save updates the subject.
   */
  getAll(nocache?: boolean): Observable<GungGroupBranding[]> {
    if (!this.brandingSubject) {
      return this.updateBrandingSubject(nocache).pipe(switchMap(() => this.brandingSubject.asObservable()));
    }
    return this.brandingSubject.asObservable();
  }

  private getInternalAll(nocache?: boolean): Observable<GungGroupBranding[]> {
    return this.http.get<GungGroupBranding[]>(this.serviceUrl, this.cacheOpts(nocache));
  }

  /** Fetch the full list and push it into the subject (creating it on first use). */
  updateBrandingSubject(nocache?: boolean): Observable<GungGroupBranding[]> {
    return this.getInternalAll(nocache).pipe(
      tap(list => {
        if (!this.brandingSubject) {
          this.brandingSubject = new BehaviorSubject<GungGroupBranding[]>(list);
        } else {
          this.brandingSubject.next(list);
        }
      })
    );
  }

  /** Lean per-group read for the navbar (fetches just one group, cacheable). */
  getByGroup(group: string, nocache?: boolean): Observable<GungGroupBranding> {
    return this.http.get<GungGroupBranding>(`${this.serviceUrl}/${group}`, this.cacheOpts(nocache));
  }

  getBrandableGroups(): Observable<string[]> {
    return this.http.get<string[]>(`${this.serviceUrl}/brandable-groups`);
  }

  save(branding: GungGroupBranding): Observable<GungGroupBranding> {
    return this.http
      .post<GungGroupBranding>(this.serviceUrl, branding)
      .pipe(tap(() => this.updateBrandingSubject(true).subscribe()));
  }

  // GET responses are cached forever by CacheInterceptorService unless a maxAge header is present
  // (a missing header never expires). nocache forces a fresh read — used to refresh the subject
  // after a save so callers never see stale data.
  private cacheOpts(nocache?: boolean): { headers?: { [h: string]: string } } {
    return nocache ? { headers: { maxAge: '-1' } } : {};
  }

  getPublicAll(nocache?: boolean): Observable<GungGroupBranding[]> {
    if (!nocache && this.publicCached) {
      return of(this.publicCached);
    }
    const headers = { maxAge: nocache ? '-1' : '60' };
    return this.http
      .get<GungGroupBranding[]>(`public/${this.serviceUrl}`, { headers })
      .pipe(tap(list => (this.publicCached = list)));
  }
}
